import * as THREE from 'three';
import { STYLE, TABLE, type TableSpec, type Theme } from '../config';

/**
 * Procedural carom table (z-up): cloth bed, rubber cushions whose inner nose
 * sits exactly on the play boundary the physics uses, a wooden frame, and
 * diamonds/sights on the rail caps. Dimensions come from the given per-variant
 * `table` spec; the cloth and wood colours come from the selected `theme`.
 */
export function buildTable(theme: Theme, table: TableSpec = TABLE): THREE.Group {
  const group = new THREE.Group();
  const halfW = table.playWidth / 2;
  const halfL = table.playLength / 2;
  const rail = table.railWidth;
  const frameW = 0.07;
  const cushH = table.cushionHeight;
  // The wooden rail cap sits a hair (2 mm) above the cushion tops. They overlap
  // by 3 mm at the inner edge; keeping the tops at different heights stops those
  // coincident faces from z-fighting (the flicker along the cushion/rail line).
  const frameH = cushH + 0.002;

  // The bed cloth extends past the play boundary, out under the cushions/rails to
  // the wooden frame, so the green surface fills the whole inner area (and never
  // leaves a gap at the cushion edge). The cushions/rails/frame are unaffected —
  // they just sit on top of the larger cloth; the playable area is still |x|<=halfW.
  const clothW = table.playWidth + 2 * rail;
  const clothL = table.playLength + 2 * rail;

  // Procedural woven-felt maps (albedo weave + normal for the nap), shared between
  // the bed and cushions. A larger ~9 cm tile repeats less often, so the mottled
  // nap reads as texture rather than a regular grid.
  const fab = fabricTextures();
  fab.map.repeat.set(clothW / 0.09, clothL / 0.09);
  fab.normalMap.repeat.copy(fab.map.repeat);

  // Low envMapIntensity keeps the matte felt deep and saturated instead of being
  // washed out / greyed by the bright indoor IBL.
  //
  // PERF: the felt is the biggest thing on screen, so every texture fetch here is
  // paid per covered pixel — on top of the 17 taps PCFSoftShadowMap already spends
  // on each shadowed fragment. That fill-rate is what pushed three-cushion (a 35%
  // larger table, and the only variant still on this procedural mesh — 4구 swaps in
  // a texture-free .glb) over the 60fps budget and straight down to a vsync-halved
  // 30. Hence: no roughnessMap (its ±0.11 weave contrast was invisible; the flat
  // 0.96 below is that map's mean), and modest anisotropy in fabricTextures().
  const clothMat = new THREE.MeshStandardMaterial({
    color: theme.cloth, roughness: 0.96, metalness: 0, envMapIntensity: 0.12,
    map: fab.map, normalMap: fab.normalMap, normalScale: new THREE.Vector2(1.3, 1.3),
  });
  const cushionMat = new THREE.MeshStandardMaterial({
    color: mix(theme.cloth, 0x000000, 0.25), roughness: 0.96, metalness: 0, envMapIntensity: 0.12,
    map: fab.map, normalMap: fab.normalMap, normalScale: new THREE.Vector2(1.6, 1.6),
    side: THREE.DoubleSide, // the extruded cushion is never see-through, whatever the winding
  });
  const woodMat = new THREE.MeshStandardMaterial({ color: theme.railWood, roughness: 0.45, metalness: 0.05 });
  const slateMat = new THREE.MeshStandardMaterial({ color: STYLE.slateColor, roughness: 1 });
  const diamondMat = new THREE.MeshStandardMaterial({ color: STYLE.diamondColor, roughness: 0.3, emissive: 0x222018, emissiveIntensity: 0.3 });

  // cloth (top surface at z = 0, balls rest at z = R); sized to reach the frame
  const cloth = new THREE.Mesh(new THREE.BoxGeometry(clothW, clothL, 0.012), clothMat);
  cloth.position.z = -0.006;
  cloth.receiveShadow = true;
  group.add(cloth);

  // slate/apron beneath
  const baseW = table.playWidth + 2 * rail + 2 * frameW;
  const baseL = table.playLength + 2 * rail + 2 * frameW;
  const slate = new THREE.Mesh(new THREE.BoxGeometry(baseW, baseL, table.bedThickness), slateMat);
  slate.position.z = -0.012 - table.bedThickness / 2;
  // The slate blocks the overhead lamp from reaching the floor under the table,
  // so ball shadows land on the cloth instead of leaking through to the floor.
  slate.castShadow = true;
  group.add(slate);

  // Cushions: a beveled rubber rather than a vertical wall. The nose sits right on
  // the play boundary the physics uses (vertical up to ball height), and only
  // ABOVE the nose does the face lean IN over the bed — so the cushion is slanted
  // inward from the top, as real billiard cushions are. The base stays flush with
  // the cloth edge (no gap), so the table no longer falls short at the rail.
  const OVERHANG = 0.004; // inner TOP edge overhangs the bed by this much
  const TUCK = 0.002; // base tucks this far OVER the cloth edge so no gap can show
  const NOSE_Z = cushH * 0.72; // ball-contact height: vertical up to here, then slants in
  const buildCushion = (length: number): THREE.Mesh => {
    // Cross-section in (u = across the rail outward from the boundary, v = height).
    // The whole inner face sits at or inside the boundary: the base overlaps the
    // cloth (TUCK), stays vertical up to the nose, then leans further in to the top.
    // Wound counter-clockwise so the extruded side faces get OUTWARD normals
    // (clockwise makes them point inward → culled → the cushion turns see-through).
    const s = new THREE.Shape();
    s.moveTo(-OVERHANG, cushH); // inner top (overhangs the bed most)
    s.lineTo(rail, cushH); // outer top
    s.lineTo(rail, 0); // outer bottom (under the wood frame)
    s.lineTo(-TUCK, 0); // inner bottom: tucked over the cloth edge (no gap)
    s.lineTo(-TUCK, NOSE_Z); // nose at ball-contact height (just inside the boundary)
    s.closePath();
    const geo = new THREE.ExtrudeGeometry(s, { depth: length, bevelEnabled: false });
    geo.translate(0, 0, -length / 2); // centre along the rail run
    geo.rotateX(Math.PI / 2); // stand it up: height → world Z, run → world Y
    const m = new THREE.Mesh(geo, cushionMat);
    m.receiveShadow = true;
    group.add(m);
    return m;
  };

  // Side rails run the full play length; the head/foot rails are widened by one
  // rail on each side so the four cushions meet at the corners (no corner gaps).
  // Each is positioned with its boundary (u = 0) on the play edge, then rotated so
  // its outward (+X local) axis points away from the table.
  buildCushion(table.playLength).position.set(halfW, 0, 0);
  const left = buildCushion(table.playLength);
  left.position.set(-halfW, 0, 0);
  left.rotation.z = Math.PI;
  const top = buildCushion(table.playWidth + 2 * rail);
  top.position.set(0, halfL, 0);
  top.rotation.z = Math.PI / 2;
  const bottom = buildCushion(table.playWidth + 2 * rail);
  bottom.position.set(0, -halfL, 0);
  bottom.rotation.z = -Math.PI / 2;

  // Wooden frame around the cushions: a single extruded ring, top flush with the
  // cushions, with slightly rounded outer corners. The inner edge overlaps the
  // cushions a hair to avoid a coincident-face seam.
  const fOff = halfW + rail + frameW / 2;
  const fOffY = halfL + rail + frameW / 2;
  const frameShape = roundedRectShape(baseW, baseL, 0.025);
  const hole = new THREE.Path();
  const ihw = halfW + rail - 0.003;
  const ihl = halfL + rail - 0.003;
  hole.moveTo(-ihw, -ihl);
  hole.lineTo(ihw, -ihl);
  hole.lineTo(ihw, ihl);
  hole.lineTo(-ihw, ihl);
  hole.lineTo(-ihw, -ihl);
  frameShape.holes.push(hole);
  const frame = new THREE.Mesh(
    new THREE.ExtrudeGeometry(frameShape, { depth: frameH, bevelEnabled: false, curveSegments: 6 }),
    woodMat,
  );
  frame.castShadow = true;
  frame.receiveShadow = true;
  group.add(frame); // extrudes from z=0 up to z=frameH

  // Diamonds on the rail caps. All 20 are the same squashed bead, so they go in a
  // single InstancedMesh: as individual meshes they were 20 separate draw calls
  // (plus 20 more whenever the shadow map refreshed) for objects 8 mm across.
  const diamondZ = frameH + 0.001;
  const diamondGeo = new THREE.SphereGeometry(0.008, 16, 12);
  diamondGeo.scale(1, 1, 0.5); // baked, so every instance matrix is a pure translation
  const spots: [number, number][] = [];
  for (let k = 1; k <= 7; k++) {
    const y = -halfL + (k * table.playLength) / 8;
    spots.push([fOff, y], [-fOff, y]);
  }
  for (let k = 1; k <= 3; k++) {
    const x = -halfW + (k * table.playWidth) / 4;
    spots.push([x, fOffY], [x, -fOffY]);
  }
  const diamonds = new THREE.InstancedMesh(diamondGeo, diamondMat, spots.length);
  const m = new THREE.Matrix4();
  spots.forEach(([x, y], i) => diamonds.setMatrixAt(i, m.makeTranslation(x, y, diamondZ)));
  diamonds.instanceMatrix.needsUpdate = true;
  group.add(diamonds);

  return group;
}

/** Lazily-built, cached woven-felt textures (albedo weave + normal map). The
 * maps are colour-neutral so every theme can tint them via the material colour. */
let fabricCache: { map: THREE.Texture; normalMap: THREE.Texture } | null = null;
function fabricTextures(): { map: THREE.Texture; normalMap: THREE.Texture } {
  if (fabricCache) return fabricCache;
  const S = 512;
  const threads = 90; // very fine thread pairs across the tile (periodic = seamless)
  const height = new Float32Array(S * S);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const u = x / S;
      const v = y / S;
      // plain weave: a faint, fine cross-hatch — kept subtle so the cloth reads as
      // smooth, even felt rather than a grid. Only a touch of fine, uniform nap.
      const wx = Math.abs(Math.sin(u * Math.PI * threads));
      const wy = Math.abs(Math.sin(v * Math.PI * threads));
      let h = 0.5 + 0.06 * (wx - wy);
      h += (Math.random() - 0.5) * 0.08; // fine, even fuzz (no blotches)
      height[y * S + x] = h;
    }
  }

  // Albedo: a near-white weave (keeps the felt's hue, multiplied by material colour).
  const colorCanvas = document.createElement('canvas');
  colorCanvas.width = colorCanvas.height = S;
  const cctx = colorCanvas.getContext('2d')!;
  const cimg = cctx.createImageData(S, S);
  for (let i = 0; i < S * S; i++) {
    const shade = Math.max(0, Math.min(1, 0.88 + (height[i] - 0.5) * 0.18));
    const c = Math.round(shade * 255);
    cimg.data[i * 4] = c;
    cimg.data[i * 4 + 1] = c;
    cimg.data[i * 4 + 2] = c;
    cimg.data[i * 4 + 3] = 255;
  }
  cctx.putImageData(cimg, 0, 0);

  // Normal map from the height gradients (wrap at the edges to stay seamless).
  const normCanvas = document.createElement('canvas');
  normCanvas.width = normCanvas.height = S;
  const nctx = normCanvas.getContext('2d')!;
  const nimg = nctx.createImageData(S, S);
  const strength = 4.5;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const hl = height[y * S + ((x - 1 + S) % S)];
      const hr = height[y * S + ((x + 1) % S)];
      const hd = height[((y - 1 + S) % S) * S + x];
      const hu = height[((y + 1) % S) * S + x];
      const nx = (hl - hr) * strength;
      const ny = (hd - hu) * strength;
      const nz = 1;
      const len = Math.hypot(nx, ny, nz);
      const i = (y * S + x) * 4;
      nimg.data[i] = Math.round((nx / len * 0.5 + 0.5) * 255);
      nimg.data[i + 1] = Math.round((ny / len * 0.5 + 0.5) * 255);
      nimg.data[i + 2] = Math.round((nz / len * 0.5 + 0.5) * 255);
      nimg.data[i + 3] = 255;
    }
  }
  nctx.putImageData(nimg, 0, 0);

  const map = new THREE.CanvasTexture(colorCanvas);
  map.colorSpace = THREE.SRGBColorSpace;
  const normalMap = new THREE.CanvasTexture(normCanvas); // stays linear (no colour space)
  for (const tx of [map, normalMap]) {
    tx.wrapS = THREE.RepeatWrapping;
    tx.wrapT = THREE.RepeatWrapping;
    // The bed is viewed at a grazing angle and tiles ~17x33 times across itself —
    // the worst case for anisotropic filtering, which costs up to this many samples
    // PER MAP per covered pixel. 4 still kills the shimmer down at the far rail; 8
    // just doubled the bill on the surface that covers most of the screen.
    tx.anisotropy = 4;
  }
  fabricCache = { map, normalMap };
  return fabricCache;
}

/** A rounded-rectangle Shape (w × h, corner radius r), centred on the origin. */
function roundedRectShape(w: number, h: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const x = -w / 2;
  const y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

/** Blend two hex colours by `t` (0 = a, 1 = b). */
function mix(a: number, b: number, t: number): number {
  const ca = new THREE.Color(a);
  const cb = new THREE.Color(b);
  return ca.lerp(cb, t).getHex();
}
